import Swal from "sweetalert2";
import { Location } from '@angular/common';
import { Component, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from "@angular/common";
import { MFAMethodsEnum, MFAMethod } from '@fry-it/risr-core';
import { MFA, READABLE_MFA_METHODS } from "@fry/system/account/mfa/mfa";
import { MFAService } from "@fry/system/account/mfa/mfa.service";
import { MatDialog } from "@angular/material/dialog";
import { CreateMfaDialogComponent } from "@fry/system/account/mfa/create-mfa-dialog.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { setPageTitle } from '@fry-it/risr-core';
import { OrganisationService } from '@fry-it/risr-core';
import { OidcService } from '@fry-it/risr-core';
import { isOKResponse } from '@fry-it/risr-core';

@Component({
    selector: 'eas-mfas',
    templateUrl: './list-mfas.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule],
})
export class ListMfasComponent {
  private oidc = inject(OidcService);
  public credentialUsername = input.required<string>();
  public user = input.required<string>();
  public remoteUser = computed(() => this.user());
  public isForRemoteUser = computed(() => this.remoteUser() !== undefined);

  mfas = signal<MFA[]>([]);
  readableName = READABLE_MFA_METHODS
  availableMethods: MFAMethod[] = [];

  private location = inject(Location);

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private organisationService: OrganisationService,
    private MFAService: MFAService,
  ) {
  }

  ngOnInit() {
    setPageTitle('Two Factor Authentication');
    this.loadUserRegisteredMFAsAndAvailableMethods();
  }

  private async loadUserRegisteredMFAsAndAvailableMethods() {
    const response = await this.MFAService.getRegisteredMFAs(this.credentialUsername(), this.remoteUser());
    if (!isOKResponse(response)) {
      return;
    }

    this.mfas.set(response.data);
    const organisation = this.organisationService.currentOrganisation();
    if (!organisation) {
      this.availableMethods = [];
    } else {
      const registeredMethods = this.mfas().map(mfa => mfa.type);
      this.availableMethods = organisation.enabledMFAs.filter(method => !registeredMethods.includes(method.id));
    }
  }

  canAdd(): boolean {
    return this.availableMethods.length > 0 && !this.isForRemoteUser();
  }

  canReplaceMFA(mfa: MFA): boolean {
    return mfa.type === MFAMethodsEnum.TOTP && !this.isForRemoteUser();
  }

  canDelete(): boolean {
    if (this.isForRemoteUser()) {
      return true;
    }
    return this.mfas().length !== 1
  }

  async createMFA(preSelectMethod?: MFAMethodsEnum) {
    await this.oidc.ensureStepup();
    const dialogRef = this.dialog.open(CreateMfaDialogComponent, {
      minWidth: '30vw',
      disableClose: true,
      data: {
        credentialUsername: this.credentialUsername(),
        availableMethods: this.availableMethods,
        preSelectMethod: preSelectMethod,
      },
    });

    dialogRef.componentInstance.completed.subscribe((result: any) => {
      dialogRef.close();
      if (!result.method) {
        return;
      }

      this.snackBar.open($localize`You've successfully setup two factor authentication!`, $localize`Close`, { duration: 3000 });
      this.loadUserRegisteredMFAsAndAvailableMethods();
    });
  }

  async deleteMFA(mfaId: string) {
    await this.oidc.ensureStepup();
    const confirmation = await Swal.fire({
      title: $localize`Are you sure?`,
      text: $localize`Are you sure you want to delete this two factor authentication method? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, delete it!`,
      cancelButtonText: $localize`No, keep it`
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    const response = await this.MFAService.deleteMFA(this.credentialUsername(), mfaId, this.remoteUser());
    if (response.state !== "ok") {
      this.snackBar.open($localize`Failed\: ${response.error}`, $localize`Close`, { duration: 3000 });
      return;
    }
    this.snackBar.open($localize`You've successfully deleted the two factor method!`, $localize`Close`, { duration: 3000 });
    this.loadUserRegisteredMFAsAndAvailableMethods();
  }

  // Aria label helpers
  replaceLabel(type: MFAMethodsEnum) {
    return $localize`Replace ${this.readableName[type]}`;
  }

  removeLabel(type: MFAMethodsEnum) {
    return $localize`Remove ${this.readableName[type]}`;
  }

  goBack() {
    this.location.back();
  }
}
